// @flow
import React from 'react';
import styled from 'styled-components';
import { maxMediaQueries } from '@finect/tabular-utils/mediaQueries';
import { ProductItem } from '@finect/tabular-components/ProductItem';
import { TrendingUp } from '@finect/tabular-icons/trendingUp';
import { ChevronDown } from '@finect/tabular-icons/chevronDown';
import { ChevronUp } from '@finect/tabular-icons/chevronUp';
import { getFont } from '@finect/tabular-utils/getFont';
import { getProductTypesByCategory } from '@finect/front-resources/Products';

const Products = styled.div`
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  margin-top: 21px;

  &:not(:last-child) {
    margin-bottom: 21px;
  }

  li {
    position: relative;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 9px;
    white-space: nowrap;
    transition: color 0.15s ease;

    &:after {
      position: absolute;
      bottom: 0;
      left: 60px;
      right: 0;
      content: "";
    }

    ::-webkit-scrollbar {
      display: none;
    }

    &:hover {
      color: ${({ theme }) => theme.linkColor};
    }
  }

  li:first-child {
    padding-top: 9px;
  }

  li:last-child {
    margin-bottom: 42px;
  }

  li:last-child:after {
    display: none;
  }

  ${maxMediaQueries.phone`
    border-bottom: none;

    li:last-child {
      margin-bottom: 48px;
    }
  `};
`;

const Title = styled.h3`
  display: flex;
  align-items: center;
  padding: 12px 90px 12px 0;
  ${getFont(-1)};

  span {
    cursor: pointer;
    color: ${({ theme }) => theme.linkColor};
  }

  span:before {
    padding: 0 9px;
    content: "–";
    color: ${({ theme }) => theme.colors.font};
  }
`;

const ProducList = styled.ul`
  overflow: hidden;
  max-height: ${({ maxHeight }) => maxHeight}px;
  transition: max-height 0.25s ease;
`;

const Icon = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 21px;
  height: 21px;
  border-radius: 21px;
  margin-right: 9px;
  background-color: ${({ theme }) => theme.colors.accent1};
  color: #fff;

  svg {
    position: absolute;
    top: 5px;
    left: 5px;
  }
`;

const Show = styled.div`
  display: flex;
  position: absolute;
  bottom: 9px;
  right: 0;
  align-items: center;
  padding: 3px 9px;
  border-radius: ${({ theme }) => theme.borderRadius};
  cursor: pointer;
  ${getFont(-1)};
  color: ${({ theme }) => theme.linkColor};
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.lightGrey};
    color: ${({ theme }) => theme.mainColor};
  }

  svg {
    ${({ up }) => up && ` position: relative; top: -1px;`};
    margin-left: 3px;
  }
`;

type Props = {
  products: Array<Object>,
  collapsed?: boolean
};

type State = {
  maxHeight: number,
  showAll: boolean
};

export class MentionedProducts extends React.Component<Props, State> {
  productList: any;

  state = {
    maxHeight: 0,
    showAll: this.props.collapsed || this.props.products.length <= 3 || false
  };

  componentDidMount = () => {
    if (!this.props.collapsed && this.productList) {
      this.setState({
        maxHeight: this.productList.scrollHeight
      });
    }
  };

  componentDidUpdate = (prevProps: Props, prevState: State) => {
    if (
      (prevProps.products.length !== this.props.products.length && this.state.maxHeight !== 0) ||
      (!prevState.showAll && this.state.showAll)
    ) {
      this.productList &&
        this.setState({
          maxHeight: this.productList.scrollHeight
        });
    }
  };

  toggleProducts = () => {
    const { maxHeight, showAll } = this.state;

    if (!showAll) return this.setState({ showAll: true });

    if (this.productList) {
      this.setState({
        maxHeight: maxHeight !== 0 ? 0 : this.productList.scrollHeight
      });
    }
  };

  render() {
    const { products } = this.props;
    const { maxHeight, showAll } = this.state;
    const plural = products.length > 1 ? 's' : '';
    const processedProducts = products.length > 3 && !this.props.collapsed ? products.slice(0, 3) : products;
    const displayProducts = processedProducts.filter(prod => prod.entity.name !== null);
    const restProducts = products.length > 3 && !this.props.collapsed ? products.slice(3 - products.length) : null;

    const iicProducts = getProductTypesByCategory('iic').map(product => product.id);

    return (
      <Products>
        <Title maxHeight={maxHeight}>
          <Icon>
            <TrendingUp size="11" stroke="2" />
          </Icon>
          <strong>
            {products.length} producto{plural} mencionado{plural}
          </strong>
        </Title>
        <ProducList ref={el => (this.productList = el)} maxHeight={maxHeight}>
          {displayProducts.map((product, index) => (
            <li key={`${index}-${product.entity.id}`}>
              <ProductItem
                subtype={product.subtype}
                entity={product.entity}
                withLink={iicProducts.includes(product.subtype)}
                withDate
              />
            </li>
          ))}
          {showAll && restProducts
            ? restProducts.map((product, index) => (
              <li key={`${index}-${product.entity.id}`}>
                <ProductItem
                  subtype={product.subtype}
                  entity={product.entity}
                  withLink={iicProducts.includes(product.subtype)}
                  withDate
                />
              </li>
            ))
            : null}
        </ProducList>
        <Show up={maxHeight !== 0 && showAll} onClick={this.toggleProducts}>
          {maxHeight === 0 ? 'Mostrar' : !showAll ? 'Ver todos' : 'Ocultar'}
          {maxHeight === 0 || !showAll ? <ChevronDown size="18" /> : <ChevronUp size="18" />}
        </Show>
      </Products>
    );
  }
}
